import { unzipSync } from 'fflate';

/** One entry of a zip archive's central directory: its path, uncompressed size, and whether it is a folder. */
export interface ZipEntry {
  path: string;
  size: number;
  isFolder: boolean;
}

/** One folder or file of the tree an archive holds. A folder's size is 0; its files carry their own. */
export interface ZipTreeNode {
  name: string;
  path: string;
  size: number;
  isFolder: boolean;
  children: ZipTreeNode[];
}

/** What an archive's directory gave: its entries, and whether they are all of them. */
export interface ZipListing {
  entries: ZipEntry[];
  /** False when the listing stopped at a limit, so `entries` is only the first part of the archive. */
  complete: boolean;
}

/** The most entries read from one archive. Every entry costs the tab time and memory to list and draw. */
export const ZIP_LISTING_MAX_ENTRIES = 10_000;

/** The most path characters read from one archive, all entries together. */
export const ZIP_LISTING_MAX_PATH_CHARACTERS = 2_000_000;

/** The most segments, folders and file name together, in one path that is read. */
export const ZIP_LISTING_MAX_DEPTH = 64;

/**
 * The most path segments read from one archive, all entries together. Each segment can become a folder of
 * the tree, so this bounds the tree a listing builds: ten thousand distinct deep paths would otherwise imply
 * hundreds of thousands of folders.
 */
export const ZIP_LISTING_MAX_PATH_SEGMENTS = 100_000;

/** Thrown from inside fflate's directory walk, because its filter has no other way to end the walk. */
class ListingLimitReached extends Error {}

/**
 * The entries of a zip archive, read from its central directory alone. The filter records each entry and
 * declines it, so nothing is inflated. The size cap bounds the bytes but not the entries — a small archive
 * can name millions of them — so the listing stops at the first entry past a limit and says it is not
 * complete. Bytes that are not a zip, or an archive whose directory cannot be read, throw.
 */
export function listZipEntries(bytes: Uint8Array): ZipListing {
  const entries: ZipEntry[] = [];
  try {
    walkDirectory(bytes, entries);
    return { entries, complete: true };
  } catch (error) {
    if (!(error instanceof ListingLimitReached)) throw error;
    return { entries, complete: false };
  }
}

/** Record the archive's entries into `entries`, ending the walk at the first one past a limit. */
function walkDirectory(bytes: Uint8Array, entries: ZipEntry[]): void {
  let pathCharacters = 0;
  let pathSegments = 0;
  unzipSync(bytes, {
    filter: (file) => {
      const depth = segmentsOf(file.name).length;
      pathCharacters += file.name.length;
      pathSegments += depth;
      if (isPastLimits(entries.length, pathCharacters, pathSegments, depth)) {
        throw new ListingLimitReached();
      }
      entries.push({
        path: file.name,
        size: file.originalSize,
        isFolder: file.name.endsWith('/'),
      });
      return false;
    },
  });
}

/** Whether reading one more entry, this deep, would take the listing past one of its limits. */
function isPastLimits(
  listed: number,
  pathCharacters: number,
  pathSegments: number,
  depth: number,
): boolean {
  return (
    listed >= ZIP_LISTING_MAX_ENTRIES ||
    pathCharacters > ZIP_LISTING_MAX_PATH_CHARACTERS ||
    pathSegments > ZIP_LISTING_MAX_PATH_SEGMENTS ||
    depth > ZIP_LISTING_MAX_DEPTH
  );
}

/** The segments of an entry's path, read the same whichever slash the tool that made the archive used. */
function segmentsOf(path: string): string[] {
  return path.split(/[\\/]/).filter((segment) => segment.length > 0);
}

/**
 * The folders of each level built so far, by name, keyed by the level's own list. Finding a folder by
 * scanning its siblings made a level of many folders cost the square of its size.
 */
type FolderIndex = Map<ZipTreeNode[], Map<string, ZipTreeNode>>;

/** A folder's child of this name, created the first time a path passes through it. */
function folderChild(
  siblings: ZipTreeNode[],
  name: string,
  path: string,
  index: FolderIndex,
): ZipTreeNode {
  const folders = index.get(siblings) ?? new Map<string, ZipTreeNode>();
  index.set(siblings, folders);
  const existing = folders.get(name);
  if (existing) return existing;
  const created: ZipTreeNode = {
    name,
    path,
    size: 0,
    isFolder: true,
    children: [],
  };
  siblings.push(created);
  folders.set(name, created);
  return created;
}

/** Folders before files, each group by name, at every level of the tree. */
function sortTree(nodes: ZipTreeNode[]): ZipTreeNode[] {
  nodes.sort((left, right) =>
    left.isFolder === right.isFolder
      ? left.name.localeCompare(right.name)
      : left.isFolder
        ? -1
        : 1,
  );
  for (const node of nodes) sortTree(node.children);
  return nodes;
}

/**
 * The archive as a tree of folders and files. A folder is made for every path segment a file passes
 * through, because many tools write no directory entries at all, and an explicit directory entry merges
 * into the folder its files imply rather than appearing twice.
 */
export function buildZipTree(entries: readonly ZipEntry[]): ZipTreeNode[] {
  const root: ZipTreeNode[] = [];
  const folderIndex: FolderIndex = new Map();
  for (const entry of entries) {
    const segments = segmentsOf(entry.path);
    const folderSegments = entry.isFolder ? segments : segments.slice(0, -1);
    let siblings = root;
    folderSegments.forEach((segment, index) => {
      const path = segments.slice(0, index + 1).join('/');
      siblings = folderChild(siblings, segment, path, folderIndex).children;
    });
    const fileName = segments.at(-1);
    if (!entry.isFolder && fileName !== undefined) {
      siblings.push({
        name: fileName,
        path: segments.join('/'),
        size: entry.size,
        isFolder: false,
        children: [],
      });
    }
  }
  return sortTree(root);
}

/** One row of an archive's top level as a tile shows it: the name, and how much a folder holds. */
export interface ZipTopLevelRow {
  name: string;
  isFolder: boolean;
  /** Folders and files directly inside a folder; 0 for a file. */
  itemCount: number;
}

/**
 * The first `limit` rows of the archive's top level. Small enough to keep as a tile's cached thumbnail,
 * so the list never downloads an archive again just to show what is at its top.
 */
export function zipTopLevel(
  entries: readonly ZipEntry[],
  limit: number,
): ZipTopLevelRow[] {
  return buildZipTree(entries)
    .slice(0, limit)
    .map((node) => ({
      name: node.name,
      isFolder: node.isFolder,
      itemCount: node.children.length,
    }));
}
